import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import cn from 'classnames';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import BlockIcon from '@material-ui/icons/Block';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import DialogContainer from 'components/DialogContainer/styles';
import DialogClose from 'components/DialogClose/styles';
import DialogBody from 'components/DialogBody/styles';
import DialogTitle from 'components/DialogTitle/styles';
import {DialogSubTitle} from 'components/DialogSubTitle/styles';
import {ButtonPrimary, ButtonOutline} from 'components/buttons';
import DialogActions from 'components/DialogActions/styles';
import DialogAction from 'components/DialogAction/styles';
import Textarea from 'components/FieldTextarea/style';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import Body1 from 'components/typography/Body1';
import {
  DELETE_DRIVER_RATING,
  RATE_DRIVER,
  UPDATE_DRIVER_RATING
} from 'store/ordersV2/orderDetails/action';
import {
  RateDriverModalContainer,
  RatingButton,
  RatingButtonsContainer,
  ReasonButton,
  ReasonsContainer,
  DetailsSection
} from 'routes/App/ordersV2/modals/RateDriverModal/styles';
import {ORDER} from 'helpers/constants/orders_v2';
import IconThumbUp from 'assets/icon_thumb_up.png';
import IconThumbDown from 'assets/icon_thumb_down.png';
import IconBlock from 'assets/icon_blocked.png';

const RATING_TYPES = {
  GOOD: 'good',
  NEEDS_IMPROVEMENT: 'needs_improvement',
  BLOCK_DRIVER: 'block_driver'
};

const RATING_TYPE_LABELS = {
  good: 'Good',
  needs_improvement: 'Needs improvement',
  block_driver: 'Block Driver'
};

const RATING_TYPE_FROM_BE = {
  Good: 'good',
  'Needs improvement': 'needs_improvement',
  'Block Driver': 'block_driver'
};

const GOOD_REASONS = [
  'Patient and respectful',
  'Clear communication',
  'Followed pickup instructions',
  'Used a hot bag',
  'Checked order for accuracy',
  'Other'
];

const NEEDS_IMPROVEMENT_REASONS = [
  'Shoved phone in face',
  'Unprofessional or rude',
  "Didn't follow pickup instructions",
  'Mishandled order',
  "Didn't match Driver profile",
  "Didn't use hot bag",
  'Slow to confirm pickup',
  'Delayed departure',
  'Other'
];

const BLOCK_REASONS = [
  'Verbally harassed others',
  'Physically aggressive',
  'Stole order',
  'Appears to be under the influence',
  'Other'
];

const RateDriverModal = ({
  isOpen,
  onClose,
  deliveryStatus,
  activeLocation,
  deliveryId,
  order,
  existingRating,
  sendDriverRateToPostHog
}) => {
  const dispatch = useDispatch();
  const {
    rateDriverLoading,
    rateDriverError,
    updateDriverRatingLoading,
    deleteDriverRatingLoading,
    driverRating
  } = useSelector((state) => state.orderDetailsV2Reducer);
  const [ratingType, setRatingType] = useState(
    RATING_TYPE_FROM_BE[existingRating?.ratingType] || null
  );
  const [selectedReasons, setSelectedReasons] = useState(
    existingRating?.selectedReasons || []
  );
  const [details, setDetails] = useState(existingRating?.details || '');
  const [errors, setErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && existingRating?.id) {
      setRatingType(RATING_TYPE_FROM_BE[existingRating?.ratingType] || null);
      setSelectedReasons(existingRating.selectedReasons || []);
      setDetails(existingRating.details || '');
    } else if (isOpen && !existingRating?.id) {
      setRatingType(null);
      setSelectedReasons([]);
      setDetails('');
    }
  }, [isOpen, existingRating]);

  const handleRatingSelect = (type) => {
    setRatingType(type);
    setSelectedReasons([]);
    setDetails('');
    setErrors({});
  };

  const handleReasonToggle = (reason) => {
    if (selectedReasons.includes(reason)) {
      setSelectedReasons(selectedReasons.filter((r) => r !== reason));
    } else {
      setSelectedReasons([...selectedReasons, reason]);
    }
    if (errors.reasons) {
      setErrors({...errors, reasons: null});
    }
  };

  const handleDetailsChange = (e) => {
    setDetails(e.target.value);
    if (errors.details) {
      setErrors({...errors, details: null});
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!ratingType) {
      return false;
    }

    if (ratingType === RATING_TYPES.BLOCK_DRIVER) {
      if (selectedReasons.length === 0) {
        newErrors.reasons = 'Please select at least one reason';
      }
      if (!details.trim()) {
        newErrors.details = 'Please provide details';
      }
    } else if (selectedReasons.length === 0) {
      newErrors.reasons = 'Please select at least one reason';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    return true;
  };

  const handleClose = () => {
    if (!existingRating?.id) {
      setRatingType(null);
      setSelectedReasons([]);
      setDetails('');
    } else {
      setRatingType(RATING_TYPE_FROM_BE[existingRating?.ratingType] || null);
      setSelectedReasons(existingRating?.selectedReasons || []);
      setDetails(existingRating?.details || '');
    }
    setErrors({});
    setShowConfirmation(false);
    setIsSubmitting(false);
    onClose();
  };

  const submitRating = () => {
    setIsSubmitting(true);
    const payload = {
      associatedOrderId: order[ORDER.ID],
      associatedInvoiceId: order[ORDER.INVOICE_ID],
      associatedOrderSource: order[ORDER.SOURCE] === 'sauce' ? 'sauce' : 'byoc',
      deliveryId,
      ratingType: RATING_TYPE_LABELS[ratingType],
      selectedReasons,
      locationId: activeLocation?._id,
      locationName: activeLocation?.BusinessName,
      dispatchProviderVersion: deliveryStatus?.dispatchVersion,
      deliveryCompany: deliveryStatus?.courier?.deliveryServiceProvider,
      deliveryProviderOrderId: deliveryStatus?.dspOrderId,
      driverName: deliveryStatus?.courier?.name,
      vehicleType: deliveryStatus?.courier?.vehicle,
      ...(details?.trim() && {details: details?.trim()})
    };

    if (existingRating?.id) {
      dispatch(UPDATE_DRIVER_RATING({deliveryId, ...payload}));
      sendDriverRateToPostHog('edit', payload);
    } else {
      dispatch(RATE_DRIVER(payload));
      sendDriverRateToPostHog('new', payload);
    }
  };

  useEffect(() => {
    if (!isSubmitting) {
      return;
    }

    const isLoading =
      rateDriverLoading ||
      updateDriverRatingLoading ||
      deleteDriverRatingLoading;
    const hasError = rateDriverError;

    if (hasError && !isLoading) {
      toast.error('Failed to submit driver rating. Please try again.');
      setIsSubmitting(false);
      return;
    }

    if (!isLoading && !hasError) {
      setIsSubmitting(false);
      handleClose();
    }
  }, [
    rateDriverLoading,
    updateDriverRatingLoading,
    deleteDriverRatingLoading,
    rateDriverError,
    isSubmitting,
    existingRating
  ]);

  useEffect(() => {
    if (
      !deleteDriverRatingLoading &&
      isSubmitting &&
      existingRating?.id &&
      !driverRating
    ) {
      toast.success('Driver rating removed successfully');
      setIsSubmitting(false);
      handleClose();
    }
  }, [deleteDriverRatingLoading, driverRating, isSubmitting, existingRating]);

  const handleDeleteRating = () => {
    setIsSubmitting(true);
    dispatch(DELETE_DRIVER_RATING(deliveryId));
    sendDriverRateToPostHog('remove', {
      associatedOrderId: order[ORDER.ID],
      associatedInvoiceId: order[ORDER.INVOICE_ID],
      associatedOrderSource: order[ORDER.SOURCE] === 'sauce' ? 'sauce' : 'byoc',
      deliveryId,
      ratingType: RATING_TYPE_LABELS[ratingType],
      selectedReasons,
      locationId: activeLocation?._id,
      locationName: activeLocation?.BusinessName,
      dispatchProviderVersion: deliveryStatus?.dispatchVersion,
      deliveryCompany: deliveryStatus?.courier?.deliveryServiceProvider,
      deliveryProviderOrderId: deliveryStatus?.dspOrderId,
      driverName: deliveryStatus?.courier?.name,
      vehicleType: deliveryStatus?.courier?.vehicle,
      ...(details?.trim() && {details: details?.trim()})
    });
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    if (ratingType === RATING_TYPES.BLOCK_DRIVER) {
      setShowConfirmation(true);
      return;
    }

    submitRating();
  };

  const handleConfirmBlock = () => {
    setShowConfirmation(false);
    submitRating();
  };

  const getReasons = () => {
    switch (ratingType) {
      case RATING_TYPES.GOOD:
        return GOOD_REASONS;
      case RATING_TYPES.NEEDS_IMPROVEMENT:
        return NEEDS_IMPROVEMENT_REASONS;
      case RATING_TYPES.BLOCK_DRIVER:
        return BLOCK_REASONS;
      default:
        return [];
    }
  };

  const getQuestion = () => {
    switch (ratingType) {
      case RATING_TYPES.GOOD:
        return `What did ${
          deliveryStatus?.courier.name || 'the driver'
        } do well?`;
      case RATING_TYPES.NEEDS_IMPROVEMENT:
        return `What could ${
          deliveryStatus?.courier.name || 'the driver'
        } improve?`;
      case RATING_TYPES.BLOCK_DRIVER:
        return `Why are you blocking ${
          deliveryStatus?.courier.name || 'the driver'
        }?`;
      default:
        return '';
    }
  };

  if (showConfirmation) {
    return (
      <Dialog open={isOpen} scroll='body' onClose={handleClose}>
        <DialogContainer className='DialogSm ConfirmModal'>
          <DialogClose onClick={handleClose} />
          <DialogTitle>
            Are you sure you&apos;d like to block{' '}
            {deliveryStatus?.courier.name || 'this driver'}?
          </DialogTitle>
          <DialogBody>
            <Body1
              style={{
                textAlign: 'center',
                marginBottom: '24px',
                marginTop: '24px'
              }}
            >
              We will report this Driver to the delivery company for review.
              Note: the Driver may still be assigned to your store through a
              different delivery company.
            </Body1>
            <DialogActions className='marginTopNull justifyCenter'>
              <DialogAction className='w40'>
                <ButtonOutline onClick={() => setShowConfirmation(false)}>
                  Cancel
                </ButtonOutline>
              </DialogAction>
              <DialogAction className='w40'>
                <ButtonPrimary
                  onClick={handleConfirmBlock}
                  style={{backgroundColor: '#ba0302'}}
                >
                  <span>Block Driver</span>
                </ButtonPrimary>
              </DialogAction>
            </DialogActions>
          </DialogBody>
        </DialogContainer>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} scroll='body' onClose={handleClose}>
      <DialogContainer className='DialogMd'>
        <DialogClose onClick={handleClose} />
        <DialogTitle className='DialogTitleLeft'>Rate your Driver</DialogTitle>
        <DialogBody>
          <RateDriverModalContainer>
            {!ratingType ? (
              <>
                <RatingButtonsContainer>
                  <RatingButton
                    onClick={() => handleRatingSelect(RATING_TYPES.GOOD)}
                    className='good'
                  >
                    <img src={IconThumbUp} alt='Good' />
                    <span>Good</span>
                  </RatingButton>
                  <RatingButton
                    onClick={() =>
                      handleRatingSelect(RATING_TYPES.NEEDS_IMPROVEMENT)
                    }
                    className='needs-improvement'
                  >
                    <img src={IconThumbDown} alt='Impr' />
                    <span>Needs improvement</span>
                  </RatingButton>
                  <RatingButton
                    onClick={() =>
                      handleRatingSelect(RATING_TYPES.BLOCK_DRIVER)
                    }
                    className='block-driver'
                  >
                    <img src={IconBlock} alt='Block' />
                    <span>Block Driver</span>
                  </RatingButton>
                </RatingButtonsContainer>
                <DialogSubTitle
                  className='textMedium'
                  style={{
                    color: '#666666',
                    fontWeight: '400',
                    fontSize: '12px'
                  }}
                >
                  Your feedback will be shared anonymously with Drivers. Please
                  be considerate and respectful as you help Drivers improve.
                </DialogSubTitle>
              </>
            ) : (
              <>
                <RatingButtonsContainer>
                  <RatingButton
                    onClick={() => handleRatingSelect(RATING_TYPES.GOOD)}
                    className={cn('good', {
                      active: ratingType === RATING_TYPES.GOOD
                    })}
                  >
                    <img src={IconThumbUp} alt='Good' />
                    <span>Good</span>
                  </RatingButton>
                  <RatingButton
                    onClick={() =>
                      handleRatingSelect(RATING_TYPES.NEEDS_IMPROVEMENT)
                    }
                    className={cn('needs-improvement', {
                      active: ratingType === RATING_TYPES.NEEDS_IMPROVEMENT
                    })}
                  >
                    <img src={IconThumbDown} alt='Impr' />
                    <span>Needs improvement</span>
                  </RatingButton>
                  <RatingButton
                    onClick={() =>
                      handleRatingSelect(RATING_TYPES.BLOCK_DRIVER)
                    }
                    className={cn('block-driver', {
                      active: ratingType === RATING_TYPES.BLOCK_DRIVER
                    })}
                  >
                    <img src={IconBlock} alt='Block' />
                    <span>Block Driver</span>
                  </RatingButton>
                </RatingButtonsContainer>
                <DialogSubTitle
                  className='textMedium'
                  style={{
                    color: '#666666',
                    fontWeight: '400',
                    fontSize: '12px'
                  }}
                >
                  Your feedback will be shared anonymously with Drivers. Please
                  be considerate and respectful as you help Drivers improve.
                </DialogSubTitle>

                <Body1
                  className='marginTopSm marginBottomSm'
                  style={{fontWeight: 600, fontSize: '16px'}}
                >
                  {getQuestion()}
                </Body1>
                <Body1 className='marginBottomMd' style={{fontSize: '14px'}}>
                  <span className='redText'>*</span> Select one or more reasons
                </Body1>

                <ReasonsContainer>
                  {getReasons().map((reason) => (
                    <ReasonButton
                      key={reason}
                      onClick={() => handleReasonToggle(reason)}
                      className={cn({
                        selected: selectedReasons.includes(reason)
                      })}
                    >
                      {reason}
                    </ReasonButton>
                  ))}
                  {errors.reasons && (
                    <ErrorSignWrapper topPosition='15px' leftPosition='0'>
                      <ErrorSign>{errors.reasons}</ErrorSign>
                    </ErrorSignWrapper>
                  )}
                </ReasonsContainer>

                <DetailsSection>
                  <Body1 className='marginBottomXs'>
                    {ratingType === RATING_TYPES.BLOCK_DRIVER && (
                      <span className='redText'>*</span>
                    )}{' '}
                    Details
                  </Body1>
                  <Textarea
                    className={cn('FieldTextareaMd', {
                      error: errors.details
                    })}
                    value={details}
                    onChange={handleDetailsChange}
                    placeholder='Provide more details'
                    style={
                      errors.details
                        ? {
                            width: '100%',
                            resize: 'none',
                            border: '1px solid #ba0302'
                          }
                        : {width: '100%', resize: 'none'}
                    }
                  />
                  {errors.details && (
                    <ErrorSignWrapper topPosition='10px' leftPosition='0'>
                      <ErrorSign>{errors.details}</ErrorSign>
                    </ErrorSignWrapper>
                  )}
                </DetailsSection>

                <DialogActions className='justifyEnd marginTopLg'>
                  {existingRating?.id && (
                    <DialogAction className='w40'>
                      <ButtonOutline
                        onClick={handleDeleteRating}
                        disabled={
                          rateDriverLoading ||
                          updateDriverRatingLoading ||
                          deleteDriverRatingLoading
                        }
                        style={{
                          borderColor: '#ff2d38',
                          color: '#ff2d38'
                        }}
                      >
                        <span>
                          {deleteDriverRatingLoading
                            ? 'Removing...'
                            : 'Remove rating'}
                        </span>
                      </ButtonOutline>
                    </DialogAction>
                  )}
                  <DialogAction className='w40'>
                    <ButtonPrimary
                      onClick={handleSubmit}
                      style={{backgroundColor: '#ff2d38'}}
                      disabled={
                        rateDriverLoading ||
                        updateDriverRatingLoading ||
                        deleteDriverRatingLoading
                      }
                    >
                      <span>
                        {(() => {
                          if (rateDriverLoading || updateDriverRatingLoading) {
                            return 'Submitting...';
                          }
                          return existingRating?.id ? 'Update' : 'Submit';
                        })()}
                      </span>
                    </ButtonPrimary>
                  </DialogAction>
                </DialogActions>
              </>
            )}
          </RateDriverModalContainer>
        </DialogBody>
      </DialogContainer>
    </Dialog>
  );
};

RateDriverModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  deliveryId: PropTypes.string,
  deliveryStatus: PropTypes.object.isRequired,
  activeLocation: PropTypes.object.isRequired,
  order: PropTypes.object.isRequired,
  existingRating: PropTypes.shape({
    id: PropTypes.string,
    locationId: PropTypes.string,
    locationName: PropTypes.string,
    associatedOrderId: PropTypes.string,
    associatedInvoiceId: PropTypes.string,
    associatedOrderSource: PropTypes.string,
    dispatchProviderVersion: PropTypes.string,
    deliveryCompany: PropTypes.string,
    deliveryProviderOrderId: PropTypes.string,
    driverName: PropTypes.string,
    vehicleType: PropTypes.string,
    ratingType: PropTypes.string,
    selectedReasons: PropTypes.arrayOf(PropTypes.string),
    details: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string
  }),
  sendDriverRateToPostHog: PropTypes.func.isRequired
};

RateDriverModal.defaultProps = {
  deliveryId: null,
  existingRating: null
};

export default RateDriverModal;
